import type { ImportFormat, LibraryEntryItem, LibrarySource } from "@/lib/api/api-client";

/**
 * Les phrases d'une entrée de bibliothèque : d'où elle vient, quand on l'a
 * ouverte, qui l'a envoyée.
 *
 * # Pourquoi un module, et pas trois lignes dans la carte
 *
 * Ces textes sont sur cinq surfaces — la tuile de « Mes morceaux », celle de
 * « Partagé avec moi », la bande de reprise, la ligne d'une playlist et
 * l'atterrissage d'un lien. Écrits dans chacune, ils y divergeraient, et c'est
 * déjà arrivé : `library-mine.tsx` étiquetait « Catalogue » une entrée
 * `shared`, parce que sa ligne testait `source === "import"` et rangeait tout
 * le reste ensemble.
 *
 * # Le ton
 *
 * Factuel, jamais un reproche. On écrit « ouvert il y a 3 semaines », jamais
 * « tu n'as pas joué depuis 3 semaines » : la même donnée, mais la seconde
 * formulation fait de la personne le sujet d'un manquement. Aucune échéance,
 * aucun compte à rebours, aucun tiret cadratin — c'est la règle du produit.
 *
 * Module pur : ni React, ni DOM (l'horloge entre par paramètre). Testé dans
 * `__tests__/entry-copy.test.ts`.
 */

const RELATIVE_TIME = new Intl.RelativeTimeFormat("fr", { numeric: "auto" });

const DAY_MS = 86_400_000;

/** Jours écoulés depuis une date ISO. Négatif ramené à zéro. */
export function daysSince(iso: string, now: number = Date.now()): number {
  const elapsed = now - new Date(iso).getTime();

  return Math.max(0, Math.floor(elapsed / DAY_MS));
}

/**
 * « aujourd'hui », « hier », « il y a 3 jours », « il y a 2 semaines ».
 *
 * L'unité s'élargit avec la distance : au-delà d'une semaine, « il y a 9 jours »
 * demande de compter, « il y a une semaine » se lit. La précision perdue ne
 * servait à rien — personne ne décide quoi jouer au jour près.
 */
export function formatLastOpened(iso: string, now: number = Date.now()): string {
  const days = daysSince(iso, now);

  if (days < 1) {
    return "aujourd'hui";
  }

  if (days < 7) {
    return RELATIVE_TIME.format(-days, "day");
  }

  if (days < 31) {
    return RELATIVE_TIME.format(-Math.floor(days / 7), "week");
  }

  return RELATIVE_TIME.format(-Math.floor(days / 30), "month");
}

/** La date d'un ajout, en clair : « 12 août ». */
const DAY_MONTH = new Intl.DateTimeFormat("fr", { day: "numeric", month: "long" });

export function formatDayMonth(iso: string): string {
  return DAY_MONTH.format(new Date(iso));
}

const SOURCE_LABELS: Record<LibrarySource, string> = {
  import: "Import",
  catalog: "Catalogue",
  shared: "Partagé",
};

/** Un import dit sous quelle forme il est arrivé : c'est ce qu'on retélécharge. */
const IMPORT_LABELS: Record<ImportFormat, string> = {
  midi: "Import MIDI",
  musicxml: "Import MusicXML",
  photo: "Import photo",
};

/**
 * D'où vient l'entrée, en un mot, ou par le nom de la personne quand c'est
 * quelqu'un.
 *
 * Une seule origine gagne un nom, et c'est le point : « Partagé par Camille »
 * est la seule qui parle d'une personne, donc la seule qui mérite plus qu'une
 * étiquette. Les deux autres ne changent rien à ce qu'on peut faire du morceau.
 */
export function entryOriginLabel(entry: LibraryEntryItem): string {
  const sharer = sharerName(entry.sharedBy);

  if (entry.source === "shared") {
    return sharer === null ? "Partagé avec toi" : `Partagé par ${sharer}`;
  }

  if (entry.source === "import" && entry.importFormat != null) {
    return IMPORT_LABELS[entry.importFormat];
  }

  return SOURCE_LABELS[entry.source];
}

/**
 * Le nom de qui a partagé, ou `null` quand il n'y en a pas à montrer.
 *
 * Un compte n'a de nom que s'il en a écrit un. Tant qu'il n'en a pas, on
 * n'affiche **rien** plutôt qu'une étiquette dérivée de son adresse e-mail :
 * un nom montré à des inconnus doit avoir été choisi par la personne qu'il
 * désigne.
 *
 * D'où le repli en « Partagé avec toi », qui dit la même chose sans nommer
 * quelqu'un qui n'a pas voulu l'être.
 */
export function sharerName(
  sharedBy: { readonly nickname: string | null } | null | undefined,
): string | null {
  const nickname = sharedBy?.nickname?.trim() ?? "";

  return nickname === "" ? null : nickname;
}

/**
 * La ligne d'origine complète : l'origine, puis la dernière ouverture.
 *
 * Un fichier sans version jouable remplace tout : la ligne dit pourquoi la
 * carte ne s'ouvre pas plutôt que d'où elle venait.
 */
export function entryOriginLine(
  entry: LibraryEntryItem,
  now: number = Date.now(),
): string {
  if (!entry.openable) {
    return "Ce fichier ne s'ouvre pas.";
  }

  return `${entryOriginLabel(entry)} · ouvert ${formatLastOpened(entry.lastOpenedAt, now)}`;
}

/**
 * « De Camille et Théo. », « De Camille, Théo et 3 autres. »
 *
 * Deux noms au maximum, puis un compte. Au-delà, la phrase d'intention d'un
 * écran devient une liste de noms, et une liste de noms n'est plus une phrase.
 * Vide quand personne n'est nommé : l'appelant n'ajoute alors rien.
 */
export function sharedByPhrase(names: readonly string[]): string {
  const unique = [...new Set(names.filter((name) => name.trim() !== ""))];

  if (unique.length === 0) {
    return "";
  }

  if (unique.length === 1) {
    return `De ${unique[0]}.`;
  }

  if (unique.length === 2) {
    return `De ${unique[0]} et ${unique[1]}.`;
  }

  return `De ${unique[0]}, ${unique[1]} et ${unique.length - 2} autres.`;
}

/**
 * L'initiale d'une pastille de personne. Une lettre, en capitale.
 *
 * `?` sur une chaîne vide : une pastille vide se lirait comme un défaut de
 * rendu, alors qu'elle dit « on ne sait pas comment cette personne s'appelle »,
 * ce qui est un état normal.
 */
export function personInitial(name: string): string {
  const trimmed = name.trim();

  return trimmed === "" ? "?" : trimmed.slice(0, 1).toUpperCase();
}

/**
 * « 5 morceaux · 7 min » sous une pochette de playlist.
 *
 * Les minutes sont arrondies à l'entier : une playlist n'est pas un programme,
 * personne ne la choisit à trente secondes près, et « 7 min 12 s » donnerait à
 * une somme d'estimations une précision qu'elle n'a pas. Une durée inconnue
 * (aucun morceau analysé) fait disparaître la seconde moitié plutôt que
 * d'afficher « 0 min ».
 */
export function playlistMetaLine(
  pieceCount: number,
  durationSec: number | null,
): string {
  const pieces = `${pieceCount} morceau${pieceCount > 1 ? "x" : ""}`;

  if (durationSec === null || durationSec <= 0) {
    return pieces;
  }

  return `${pieces} · ${Math.max(1, Math.round(durationSec / 60))} min`;
}

/** La somme des durées connues d'une liste, `null` si aucune ne l'est. */
export function totalDurationSec(
  pieces: readonly { durationSec: number | null }[],
): number | null {
  let total = 0;
  let known = false;

  for (const piece of pieces) {
    if (piece.durationSec !== null) {
      total += piece.durationSec;
      known = true;
    }
  }

  return known ? total : null;
}
